import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { useInvalidateAccountSettingsQuery } from '@/entities/account';
import { useInvalidateProfileQuery } from '@/entities/profile';
import { patchChangeEmail } from '@/shared/api/auth';
import type { GoChatBackendInternalModelsChangeEmailRequest } from '@/shared/api/auth/generate/auth.gen';
import { useNotifications } from '@/shared/ui/Notifications';

import { ChangeEmailFormDataType } from './types';

export function useChangeEmail() {
  const { t } = useTranslation();
  const { addNotification } = useNotifications();
  const invalidateAccountSettings = useInvalidateAccountSettingsQuery();
  const invalidateProfile = useInvalidateProfileQuery();

  const changeEmailMutation = useMutation({
    mutationFn: (vars: GoChatBackendInternalModelsChangeEmailRequest) =>
      patchChangeEmail(vars),
    onSuccess: async () => {
      addNotification({
        type: 'success',
        title: t('settings:changeEmail.notifications.success'),
      });
      await Promise.all([invalidateAccountSettings(), invalidateProfile()]);
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: error.message,
        caption: error?.data?.details,
      });
    },
  });

  const changeEmail = async (
    { email, currentPassword }: ChangeEmailFormDataType,
    onChanged?: () => void
  ) => {
    changeEmailMutation.mutate(
      {
        email,
        current_password: currentPassword,
      },
      { onSuccess: onChanged }
    );
  };

  return {
    changeEmail,
    isPending: changeEmailMutation.isPending,
    isError: changeEmailMutation.isError,
    isSuccess: changeEmailMutation.isSuccess,
    error: changeEmailMutation.error,
    errorReset: changeEmailMutation.reset,
    data: changeEmailMutation.data,
  };
}
